'use client';

import { c as _c } from "react/compiler-runtime";
import { isDefined } from '@morpho-org/morpho-ts';
import { CardText, Skeleton } from '@morpho-org/ui-v6/server';
import { Button, Separator } from '@repo/ui';
import { ArrowRight, Close } from '@repo/ui/icons';
import React, { memo, type PropsWithChildren } from 'react';
import { useIsMedium } from '@/hooks/useBreakpoints';
export interface OperationSummaryDialogProps extends PropsWithChildren {
  loading?: boolean;
  error?: string | null;
  title: string;
  topSlot?: React.ReactNode;
  headerActionSlot?: React.ReactNode;
  /** Optional amount inputs. Omit for input-less flows (e.g. full-position refinance). */
  inputSlots?: React.ReactNode | React.ReactNode[];
  initialPositionSlot: React.ReactNode;
  finalPositionSlot: React.ReactNode;
  actionSlot: React.ReactNode;
  size?: 'small' | 'medium';
  onClose: () => void;
}
const skeletonHeight = {
  small: '257px',
  medium: '312px'
};
const OperationSummaryDialog = t0 => {
  const $ = _c(52);
  const {
    loading,
    title,
    headerActionSlot,
    inputSlots,
    topSlot,
    initialPositionSlot,
    finalPositionSlot,
    actionSlot,
    error,
    children,
    size: t1,
    onClose
  } = t0;
  const size = t1 === undefined ? "medium" : t1;
  const isTablet = useIsMedium();
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== actionSlot || $[1] !== error || $[2] !== finalPositionSlot || $[3] !== headerActionSlot || $[4] !== initialPositionSlot || $[5] !== inputSlots || $[6] !== isTablet || $[7] !== loading || $[8] !== onClose || $[9] !== size || $[10] !== title || $[11] !== topSlot) {
    const inputs = [inputSlots].flat().filter(isDefined);
    const hasInputs = inputs.length > 0;
    t2 = "gap-md flex flex-col";
    t3 = "operation-summary-dialog";
    let t6;
    if ($[16] !== isTablet || $[17] !== loading || $[18] !== title) {
      t6 = loading ? <Skeleton className="h-[45px] w-[280px] rounded-[20px]" /> : <span className={isTablet ? "text-title-xs" : "text-title-sm"}>{title}</span>;
      $[16] = isTablet;
      $[17] = loading;
      $[18] = title;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== error || $[21] !== headerActionSlot || $[22] !== isTablet || $[23] !== loading) {
      t7 = !isDefined(error) && !loading && !isTablet && headerActionSlot;
      $[20] = error;
      $[21] = headerActionSlot;
      $[22] = isTablet;
      $[23] = loading;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Close />;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    let t9;
    if ($[26] !== onClose) {
      t9 = <Button size="icon" onClick={onClose} variant="secondary" aria-label="Close">{t8}</Button>;
      $[26] = onClose;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] !== t7 || $[29] !== t9) {
      t10 = <div className="gap-sm flex flex-row items-center">{t7}{t9}</div>;
      $[28] = t7;
      $[29] = t9;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    let t11;
    if ($[31] !== t10 || $[32] !== t6) {
      t11 = <div className="flex items-center justify-between gap-[16px]">{t6}{t10}</div>;
      $[31] = t10;
      $[32] = t6;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== error || $[35] !== headerActionSlot || $[36] !== isTablet || $[37] !== loading) {
      t12 = isTablet && !isDefined(error) && !loading && headerActionSlot;
      $[34] = error;
      $[35] = headerActionSlot;
      $[36] = isTablet;
      $[37] = loading;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    let t13;
    if ($[39] !== actionSlot || $[40] !== error) {
      t13 = isDefined(error) && actionSlot;
      $[39] = actionSlot;
      $[40] = error;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    if ($[42] !== t11 || $[43] !== t12 || $[44] !== t13) {
      t4 = <div className="gap-lg flex flex-col">{t11}{t12}{t13}</div>;
      $[42] = t11;
      $[43] = t12;
      $[44] = t13;
      $[45] = t4;
    } else {
      t4 = $[45];
    }
    t5 = isDefined(error) ? <CardText height="500px" text={error} className="text-center" /> : <div className="gap-lg flex flex-col"><div className="gap-sm flex flex-col">{topSlot}{hasInputs && <><Separator style={{
            marginBlock: "3px"
          }} /><div className={`gap-lg flex w-full ${isTablet ? "flex-col" : "flex-row"}`}>{loading ? inputs.map(_temp) : inputs}</div></>}<Separator style={{
          marginBlock: "3px"
        }} /><div className={`gap-xs flex w-[100%] items-center justify-between ${isTablet ? "flex-col" : "flex-row"}`}>{loading && <Skeleton className="w-[100%] rounded-[20px]" style={{
            height: skeletonHeight[size]
          }} />}{!loading && <div className="min-w-0 flex-1">{initialPositionSlot}</div>}{loading ? <div className="h-lg flex w-8 rounded-[100px]"><Skeleton className="h-lg w-8 rounded-[20px]" /></div> : <Button variant="secondary" size="icon" aria-label="Position change" style={{
            pointerEvents: "none",
            transform: isTablet ? "rotate(90deg)" : "none"
          }}><ArrowRight /></Button>}{loading && <Skeleton className="w-[100%] rounded-[20px]" style={{
            height: skeletonHeight[size]
          }} />}{!loading && <div className="min-w-0 flex-1">{finalPositionSlot}</div>}</div></div>{loading && <div className="gap-xs flex flex-col items-stretch justify-start" style={{
        paddingBottom: 12
      }}><Skeleton className="h-[48px] w-[100%] rounded-[20px]" /></div>}{!loading && actionSlot}</div>;
    $[0] = actionSlot;
    $[1] = error;
    $[2] = finalPositionSlot;
    $[3] = headerActionSlot;
    $[4] = initialPositionSlot;
    $[5] = inputSlots;
    $[6] = isTablet;
    $[7] = loading;
    $[8] = onClose;
    $[9] = size;
    $[10] = title;
    $[11] = topSlot;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[46] !== children || $[47] !== t2 || $[48] !== t3 || $[49] !== t4 || $[50] !== t5) {
    t6 = <div className={t2} data-testid={t3}>{t4}{t5}{children}</div>;
    $[46] = children;
    $[47] = t2;
    $[48] = t3;
    $[49] = t4;
    $[50] = t5;
    $[51] = t6;
  } else {
    t6 = $[51];
  }
  return t6;
};
export default memo(OperationSummaryDialog);
function _temp(_, index) {
  return <Skeleton className="h-[115px] w-[100%] rounded-[20px]" key={`input-skeleton-${index}`} />;
}